/**
 * WebSocket Connection Monitor
 * Handles monitoring and automatic reconnection of WebSocket connections
 */
class ConnectionMonitor {
  /**
   * Create a new connection monitor
   * @param {Object} coreService - The core WebSocket service
   */
  constructor(coreService) {
    this.core = coreService;
    this.connectionMonitor = null;
    this.reconnectAttempts = 0;
    this._reconnectCount = 0;
    this._lastReconnectTime = 0;
    this._reconnecting = false;
    this._lastAttendantParams = null;
    this._lastConnectSuccess = null;
    this._lastDisconnectTime = null;
    this._reconnectTimer = null;
  }

  /**
   * Start monitoring the WebSocket connection
   * @param {Object} handlers - Handlers for connection events
   */
  startMonitoring(handlers = {}) {
    if (!this.core.cable) return;

    // Clear any existing connection monitor first
    this.stopMonitoring();

    // Reset connection check counter to prevent spam
    this.reconnectAttempts = 0;

    // Store handlers
    this.handlers = handlers || {};

    // Use ActionCable's built-in connection monitoring instead of our custom implementation
    // ActionCable already has a robust reconnection mechanism
    console.log('[Connection Monitor] Using ActionCable built-in monitoring');

    // Simply ensure ActionCable's monitor is running
    if (this.core.cable && this.core.cable.connection && this.core.cable.connection.monitor) {
      // ActionCable's monitor handles reconnection automatically
      // We just need to ensure it's started
      this.core.cable.connection.monitor.start();
      console.log('[Connection Monitor] ActionCable monitor started');
    }

    return;

    // Check connection status every 15 seconds (reduced frequency to avoid false positives)
    this.connectionMonitor = setInterval(() => {
      if (!this.core.cable || !this.core.cable.connection) {
        return; // Skip if cable isn't properly initialized
      }

      // Debug: Log the actual connection state
      const isDisconnected = this.core.cable.connection.disconnected;
      const webSocket = this.core.cable.connection.webSocket;
      const webSocketState = webSocket ? webSocket.readyState : 'no websocket';

      // WebSocket readyState: 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
      // Only log periodically to avoid spam
      if (this.reconnectAttempts === 0 || this.reconnectAttempts % 5 === 0) {
        console.log(`[Connection Monitor Debug] disconnected: ${isDisconnected}, wsState: ${webSocketState}, wsExists: ${!!webSocket}`);
      }

      // Only consider disconnected if WebSocket is actually closed (readyState === 3)
      // ActionCable sometimes reports disconnected=true even when WebSocket is open
      // Also ignore if WebSocket is connecting (readyState === 0)
      const reallyDisconnected = isDisconnected && webSocket && webSocket.readyState === 3;

      if (reallyDisconnected) {
        this.reconnectAttempts++;
        
        // Limit warning messages to avoid console spam
        if (this.reconnectAttempts <= 3 || this.reconnectAttempts % 5 === 0) {
          console.warn(`WebSocket connection lost, attempting to reconnect... (attempt ${this.reconnectAttempts})`);
        }
        
        // Only attempt reconnection if we're not already initializing and based on reconnect attempt limits
        // Also add a time-based throttle to prevent rapid reconnection attempts
        const now = Date.now();
        const timeSinceLastReconnect = now - (this._lastReconnectTime || 0);

        if (!this.core._initializing && this.reconnectAttempts <= 3 && timeSinceLastReconnect > 5000) {
          console.log('WEBSOCKET DEBUG: Reconnecting due to disconnection');
          this._lastReconnectTime = now;
          this.core.initialize(true);
          
          // Re-establish subscriptions if we had active ones - but only once
          if (this.reconnectAttempts === 1) {
            setTimeout(() => {
              // Reconnect attendant if we have parameters stored
              if (this._lastAttendantParams && this.handlers.onReconnectAttendant) {
                console.log('WEBSOCKET DEBUG: Re-establishing attendant subscription after reconnect');
                this.handlers.onReconnectAttendant(
                  this._lastAttendantParams.partnerId,
                  this._lastAttendantParams.attendantId,
                  this._lastAttendantParams.token
                );
              }
            }, 2000); // Wait for reconnection to complete
          }
        }
      } else {
        // Reset counter if connection is good
        if (this.reconnectAttempts > 0) {
          console.log('WebSocket connection restored');
          this.reconnectAttempts = 0;
          
          if (this.handlers.onConnectionRestored) {
            this.handlers.onConnectionRestored();
          }
        }
      }
    }, 15000); // Check every 15 seconds
  }

  /**
   * Stop monitoring the WebSocket connection
   */
  stopMonitoring() {
    if (this.connectionMonitor) {
      clearInterval(this.connectionMonitor);
      this.connectionMonitor = null;
    }
  }

  /**
   * Store attendant parameters for reconnection
   * @param {string} partnerId - Partner ID
   * @param {string} attendantId - Attendant ID
   * @param {string} token - Authentication token
   */
  storeAttendantParams(partnerId, attendantId, token) {
    this._lastAttendantParams = {
      partnerId: String(partnerId),
      attendantId: String(attendantId),
      token: token || '' // Empty string is safer than undefined for serialization
    };
    this._lastConnectSuccess = Date.now();
  }

  /**
   * Handle disconnection and schedule reconnection if needed
   * @param {string} partnerId - Partner ID
   * @param {string} attendantId - Attendant ID
   */
  handleDisconnection(partnerId, attendantId) {
    console.log(`Attendant WebSocket disconnected for partner: ${partnerId}, attendant: ${attendantId}`);
    
    // Track disconnection time
    const now = Date.now();
    this._lastDisconnectTime = now;
    
    // Only try to reconnect if:
    // 1. We haven't tried too many times recently
    // 2. It's been a reasonable time since our last connection success
    // 3. We're not currently reconnecting
    const reconnectCount = this._reconnectCount || 0;
    const timeSinceLastSuccess = now - (this._lastConnectSuccess || 0);
    
    // If we've been connected for a while before disconnecting, it's probably a legitimate disconnection
    const isLegitimateDisconnect = timeSinceLastSuccess > 30000; // 30 seconds
    
    if (reconnectCount <= 3 && (isLegitimateDisconnect || reconnectCount === 0) && !this._reconnecting) {
      console.log('WEBSOCKET DEBUG: Scheduling attendant reconnection');
      
      // Clear any existing reconnect timer
      clearTimeout(this._reconnectTimer);
      
      // Set longer delay for reconnection to prevent rapid cycles
      this._reconnectTimer = setTimeout(() => {
        if (this._lastAttendantParams && this.handlers.onReconnectAttendant) {
          console.log('WEBSOCKET DEBUG: Auto-reconnecting attendant after disconnection');
          this._reconnecting = true;
          
          try {
            this.handlers.onReconnectAttendant(
              this._lastAttendantParams.partnerId,
              this._lastAttendantParams.attendantId,
              this._lastAttendantParams.token
            );
          } finally {
            this._reconnecting = false;
          }
        }
      }, 5000);
    } else {
      console.log('WEBSOCKET DEBUG: Skipping auto-reconnect due to reconnection limits or already in progress');
    }
  }
}

export default ConnectionMonitor;
